import {
  comparePPartsDesc,
  getPPartId,
  pPartFromFirestore,
} from '@parallel-fluidics/shared/models/projects/parts';
import AddPartModal from 'components/Projects/addPartModal';
import {
  collection,
  getFirestore,
  orderBy,
  query,
} from 'firebase/firestore';
import {
  useGetOrdersByProjectId,
  useGetProjectByDocId,
} from 'hooks/use-basicQueries';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

const PPartDataContext = createContext();

export default function PPartProvider({ children }) {
  const { projectDocId } = useParams();
  const { data: project } = useGetProjectByDocId(projectDocId);
  const { data: orders } = useGetOrdersByProjectId(project?.name);

  // gather used part ids from orders based on project name
  const usedPPartIdsSet = useMemo(() => {
    const ids = new Set();
    orders?.forEach((order) => {
      order.shipments.forEach((shipment) => {
        shipment.lineItems.forEach((lineItem) => {
          lineItem.steps?.forEach((step) => {
            if (step.pPartId) {
              ids.add(step.pPartId);
            }
          });
        });
      });
    });
    return ids;
  }, [orders]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);

  const queryParams = useMemo(
    () => query(
      collection(getFirestore(), 'projects', projectDocId, 'parts'),
      orderBy('partId', 'desc'),
    ),
    [projectDocId],
  );

  const { data: pParts, status } = useRealtimeDocs({
    query: queryParams,
    transformData: pPartFromFirestore,
  });

  const sortedPParts = useMemo(() => {
    if (!pParts?.length) return [];
    return [...pParts].sort(comparePPartsDesc);
  }, [pParts]);

  const edgesQueryParams = useMemo(
    () => query(
      collection(getFirestore(), 'projects', projectDocId, 'edges'),
    ),
    [projectDocId],
  );

  const { data: savedEdges } = useRealtimeDocs({
    query: edgesQueryParams,
  });

  const value = useMemo(() => ({
    pParts: sortedPParts,
    status,
    project,
    projectDocId,
    savedEdges,
    usedPPartIdsSet,
    setOpenAddModal,
    setEditingPart,
  }), [sortedPParts, status, project, projectDocId, savedEdges, usedPPartIdsSet]);

  return (
    <PPartDataContext.Provider value={value}>
      {children}
      {openAddModal && (
        <AddPartModal
          pParts={sortedPParts}
          projectDocId={projectDocId}
          isInternal={project?.isInternal === true}
          open={openAddModal}
          onClose={() => {
            setOpenAddModal(false);
            setEditingPart(null);
          }}
          isEditing={!!editingPart}
          isUsed={editingPart && usedPPartIdsSet.has(getPPartId(editingPart))}
          initPart={editingPart}
        />
      )}
    </PPartDataContext.Provider>
  );
}
PPartProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const usePPartData = () => {
  const context = useContext(PPartDataContext);
  if (context === undefined) {
    throw new Error('usePPartData must be used within a PPartProvider');
  }
  return context;
};
